import PageLayout from "@/components/PageLayout";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Target, Eye, Rocket, Users, Briefcase, Award, Clock, TrendingUp } from "lucide-react";

const stats = [
  { value: 5, suffix: "+", label: "Active Clients", icon: Users },
  { value: 7, suffix: "+", label: "Projects in Progress", icon: Briefcase },
  { value: 73, suffix: "+", label: "Satisfied Clients", icon: Award },
  { value: 300, suffix: "+", label: "Completed Orders", icon: TrendingUp },
  { value: 80, suffix: "+", label: "Brands Joined", icon: Rocket },
  { value: 3, suffix: "+", label: "Years of Experience", icon: Clock },
];

const Counter = ({ target, suffix, label, icon: Icon }: { target: number; suffix: string; label: string; icon: React.ElementType }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1500;
          const step = target / (duration / 16);
          let current = 0;
          const timer = setInterval(() => {
            current += step;
            if (current >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <div ref={ref} className="text-center bg-card border border-border rounded-2xl p-8 hover:shadow-xl transition-all hover:-translate-y-1">
      <div className="w-14 h-14 rounded-2xl gradient-hero flex items-center justify-center mx-auto mb-4">
        <Icon className="w-6 h-6 text-primary-foreground" />
      </div>
      <p className="text-4xl lg:text-5xl font-display font-extrabold text-primary mb-2">
        {count}{suffix}
      </p>
      <p className="text-sm text-muted-foreground font-medium">{label}</p>
    </div>
  );
};

const AchievementsPage = () => {
  return (
    <PageLayout title="Our Achievements" subtitle="Milestones that define our journey of digital excellence">
      {/* Stats Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <span className="inline-block px-5 py-2 rounded-full gradient-cta text-primary-foreground text-xs font-semibold tracking-widest uppercase mb-4">Our Active Stats</span>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-foreground mb-4">Numbers That Speak</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              With over 3 years of industry experience, we've empowered businesses worldwide through measurable success.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {stats.map((s) => (
              <Counter key={s.label} target={s.value} suffix={s.suffix} label={s.label} icon={s.icon} />
            ))}
          </div>
        </div>
      </section>

      {/* Mission / Vision / Goals */}
      <section className="py-20 gradient-hero">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <Target className="w-7 h-7 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-bold text-primary-foreground mb-3">Our Mission</h3>
              <p className="text-primary-foreground/80 text-sm leading-relaxed">
                To empower businesses with creative, strategic, and innovative digital services that drive measurable results and long-term success.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <Eye className="w-7 h-7 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-bold text-primary-foreground mb-3">Our Vision</h3>
              <p className="text-primary-foreground/80 text-sm leading-relaxed">
                To become a leading global digital agency, recognized for excellence, creativity, and impactful solutions that help brands thrive online.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <Rocket className="w-7 h-7 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-bold text-primary-foreground mb-3">Our Goals</h3>
              <p className="text-primary-foreground/80 text-sm leading-relaxed">
                Deliver premium digital solutions for global brands, enhance brand visibility and audience engagement, and provide result-driven strategies for sustainable growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">Ready to Grow Your Brand?</h2>
          <p className="text-muted-foreground mb-8 max-w-lg mx-auto">Let's work together to take your business to the next level.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/+923058559591"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full gradient-cta text-primary-foreground font-display font-semibold text-sm hover:opacity-90 transition-opacity"
            >
              Get A Free Quote on WhatsApp
            </a>
            <Link
              to="/services"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full border-2 border-primary text-primary font-display font-semibold text-sm hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              View Our Services
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default AchievementsPage;
